import React, { useRef, useState, useMemo, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Grid, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, IconButton, Typography, FormControl, Select, MenuItem, FormHelperText, Autocomplete, CircularProgress, Card, CardContent, Paper,
    GlobalStyles, FormControlLabel, Switch,
    useTheme,
    useMediaQuery,
    Skeleton,
    Tooltip,
} from "@mui/material";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import * as tus from "tus-js-client";
import { Add, Close, CloudUpload, Schedule, Publish, ArrowBack } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { frFR, enUS } from "@mui/x-data-grid/locales";
// Redux Actions
import { fetchAlbumBySlug } from "../../Redux/Reducers/AlbumsReducer";
import { fetchAudioLibraries } from "../../Redux/Reducers/AudioLibrariesReducer";
import { fetchTracks, createTrack, updateTrack, deleteTrack, fetchTrackById, scheduleTrackRequest, publishTrackRequest } from "../../Redux/Reducers/TracksReducer";
import ScheduleRequestModal from "./partials/ScheduleRequestModal";
import { formatEnumLabel } from "../../utils/formatEnumLabel";
import { useNavigate } from "react-router-dom";
import { getFullUrl } from "../../utils/globalUtils";
import { Image } from "react-bootstrap";
import { CustomLoadingOverlay } from "../../components/common/TableLoader";
import { formatUTCDate, formatUTCDateForDataGrid } from "../../utils/dateUtils";

/**
 * COMPONENT: ADD PODCAST MODAL 
 * Integrated with Library selection and Drag & Drop
 */
const ViewTrackModal = ({ open, handleClose, viewTrack }) => {
    const { t } = useTranslation();


    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
                {t('albums.albumDetails.track_details', "Track Details")}
                <IconButton onClick={handleClose} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    borderColor: 'rgba(230, 210, 249, 0.1)',
                    padding: '24px 16px',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(230, 210, 249, 0.3)',
                        borderRadius: '4px',
                        '&:hover': {
                            background: 'rgba(230, 210, 249, 0.5)',
                        },
                    },
                }}
            >
                {!viewTrack ? (
                    <Box display="flex" justifyContent="center" alignItems="center" p={4} minHeight={200}>
                        <CircularProgress sx={{ color: '#FF69B4' }} />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {/* Cover Image */}
                        {viewTrack?.coverImage && (
                            <Grid item xs={12} textAlign="center">
                                <Box
                                    component="img"
                                    src={getFullUrl(viewTrack?.coverImage, 'album')}
                                    alt="cover"
                                    sx={{
                                        width: 180,
                                        borderRadius: "12px",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.3)"
                                    }}
                                />
                            </Grid>
                        )}

                        {/* Track Section */}
                        <Grid item xs={12}>
                            <Paper sx={{ p: '16px', borderRadius: '10px !important', border: "1px solid rgba(230, 210, 249, 0.2)", backgroundColor: "rgba(255, 255, 255, 0.1)", boxShadow: "none" }}>
                                <Typography variant="subtitle2" sx={{ color: "white", fontSize: "16px", fontWeight: "500" }}>
                                    {t('albums.albumDetails.fields.title', "Title")}
                                </Typography>
                                <Typography variant="body1" sx={{ fontSize: "14px", fontWeight: "400", color: "#d5d5d5" }}>
                                    {viewTrack?.title || "-"}
                                </Typography>

                                <Typography variant="subtitle2" sx={{ color: "white", fontSize: "16px", fontWeight: "500", mt: "16px" }}>
                                    {t('albums.albumDetails.fields.description', "Description")}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: "14px", fontWeight: "400", color: "#d5d5d5" }}>
                                    {viewTrack?.description || "-"}
                                </Typography>
                            </Paper>
                        </Grid>

                        {/* Track Info */}
                        <Grid item xs={12}>
                            <Paper sx={{ p: '16px', borderRadius: '10px !important', border: "1px solid rgba(230, 210, 249, 0.2)", backgroundColor: "rgba(255, 255, 255, 0.1)", boxShadow: "none" }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={3}>
                                        <Typography variant="subtitle2" sx={{ color: "white", fontSize: "16px", fontWeight: "500" }}>
                                            {t('albums.albumDetails.fields.track_number', "Track Number")}
                                        </Typography>
                                        <Typography sx={{ fontSize: "14px", fontWeight: "400", color: "#d5d5d5" }}>
                                            {viewTrack?.trackNumber}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <Typography variant="subtitle2" sx={{ color: "white", fontSize: "16px", fontWeight: "500" }}>
                                            {t('albums.albumDetails.fields.status', "Status")}
                                        </Typography>
                                        <Typography sx={{ fontSize: "14px", fontWeight: "400", color: "#d5d5d5" }}>
                                            {formatEnumLabel(viewTrack?.status)}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <Typography variant="subtitle2" sx={{ color: "white", fontSize: "16px", fontWeight: "500" }}>
                                            {t('albums.albumDetails.fields.explicit', "Explicit Content")}
                                        </Typography>
                                        <Typography sx={{ fontSize: "14px", fontWeight: "400", color: "#d5d5d5" }}>
                                            {viewTrack?.isExplicit ? t("yes", "Yes") : t("no", "No")}
                                        </Typography>
                                    </Grid>

                                    {String(viewTrack?.status || '').toUpperCase() === 'SCHEDULED' && (
                                    <Grid item xs={12} md={3}>
                                        <Typography variant="subtitle2" sx={{ color: "white", fontSize: "16px", fontWeight: "500" }}>
                                            {t('albums.albumDetails.fields.scheduled_date', "Scheduled Date")}
                                        </Typography>
                                        <Typography sx={{ fontSize: "14px", fontWeight: "400", color: "#d5d5d5" }}>
                                            {viewTrack?.scheduledAt
                                                ? formatUTCDate(viewTrack.scheduledAt, { separator: '-', showSeconds: false })
                                                : 'N/A'}
                                        </Typography>
                                    </Grid>
                                    )}

                                </Grid>
                            </Paper>
                        </Grid>

                        {/* Audio Player */}
                        {viewTrack?.audioFileUrl && (
                            <Grid item xs={12}>
                                <Paper sx={{ p: '16px', borderRadius: '10px !important', border: "1px solid rgba(230, 210, 249, 0.2)", backgroundColor: "rgba(255, 255, 255, 0.1)", boxShadow: "none" }}>
                                    <Typography variant="subtitle2" sx={{ color: "white", fontSize: "16px", fontWeight: "500", }}>
                                        {t('albums.albumDetails.fields.audio_preview', "Audio Preview")}
                                    </Typography>

                                    <Box mt={3}>
                                        <Typography variant="subtitle3" sx={{ color: "white", fontSize: "16px", fontWeight: "500", marginBottom: "3" }}>
                                            {viewTrack.audioFilename || "N/A"}
                                        </Typography>
                                    </Box>
                                    <Box
                                        mt={3}
                                        sx={{
                                            background: "#121212",
                                            borderRadius: "100px",
                                            padding: "5px 5px 0 5px",
                                            border: "1px solid rgba(230, 210, 249, 0.1)",
                                        }}
                                    >

                                        {(viewTrack.audioFileUrl) && (
                                            <audio
                                                controls
                                                style={{
                                                    width: "100%",
                                                    filter: "invert(1) hue-rotate(180deg)",
                                                }}
                                                src={getFullUrl(viewTrack.audioFileUrl)}
                                            />
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                )}
            </DialogContent>

            <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', gap: '6px' }}>
                <Button
                    onClick={handleClose}
                    variant="contained"
                    sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}
                >
                    {t('close', "Close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const AddTrackModal = ({
    open,
    handleClose,
    t,
    albumId,
    paginationModel,
    searchText = "",
    editingTrack,
    nextTrackNumber
}) => {
    const fileInputRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);
    const { audios = [] } = useSelector((state) => state.audios || {});
    const dispatch = useDispatch();
    const [coverImagePreview, setCoverImagePreview] = useState(null);
    const [audioPreview, setAudioPreview] = useState(null);
    const [actionType, setActionType] = useState("draft");
    const actionTypeRef = useRef("draft");
    const { user } = useSelector((state) => state.auth || state.AuthReducer || {});

    const [uploading, setUploading] = useState(false);
    const [audioDuration, setAudioDuration] = useState(0);

    const MAX_AUDIO_SIZE = 100 * 1024 * 1024; // 100MB
    const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

    const ALLOWED_AUDIO_TYPES = [
        "audio/mpeg",
        "audio/mp3",
        "audio/mp4",
    ];

    const ALLOWED_IMAGE_TYPES = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
    ];

    const getAudioDuration = (file) => {
        return new Promise((resolve, reject) => {
            const audio = document.createElement("audio");

            audio.preload = "metadata";

            audio.onloadedmetadata = () => {
                window.URL.revokeObjectURL(audio.src);
                resolve(audio.duration);
            };

            audio.onerror = () => {
                reject("Invalid audio file");
            };

            audio.src = URL.createObjectURL(file);
        });
    };

    const handleAudioSelection = async (file, field) => {
        if (!file) return;

        const isValid = await validateAudioFile(file);

        if (!isValid) return;

        if (audioPreview) {
            URL.revokeObjectURL(audioPreview);
        }

        const previewUrl = URL.createObjectURL(file);

        field.onChange(file);

        setAudioPreview(previewUrl);
    };

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            contentLanguage: "en",
            title: "",
            description: "",
            uploadType: "simple_upload", // Default to simple
            audioFile: null,
            libraryAudioId: "",
            status: "DRAFT",
            trackNumber: 1,
            albumId: albumId || "",
            isExplicit: false
        }
    });

    const uploadType = watch("uploadType");

    // Reset form when modal opens
    useEffect(() => {
        if (open && !editingTrack) {
            reset({
                contentLanguage: "en",
                title: "",
                description: "",
                uploadType: "simple_upload",
                status: "DRAFT",
                trackNumber: nextTrackNumber || 1,
                albumId: albumId || "",
                libraryAudioId: "",
                isExplicit: false
            });

            setCoverImagePreview(null);

            setAudioPreview(null);
        }

        dispatch(fetchAudioLibraries({ fetchType: "dropdown", limit: 10000 }));

    }, [open, editingTrack, nextTrackNumber]);

    useEffect(() => {
        return () => {
            if (audioPreview) {
                URL.revokeObjectURL(audioPreview);
            }
        };
    }, [audioPreview]);

    const handleCoverImageChange = (e) => {
        const file = e.target.files[0];

        if (!file) return;

        // Validate image type
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            toast.error(
                t(
                    "albums.albumDetails.messages.invalid_image_type",
                    "Only JPG, PNG and WEBP images are allowed"
                )
            );
            return;
        }

        // Validate image size
        if (file.size > MAX_IMAGE_SIZE) {
            toast.error(
                t(
                    "albums.albumDetails.messages.image_size_limit",
                    "Image size should not exceed 5MB"
                )
            );
            return;
        }

        setValue("coverImage", file);

        if (coverImagePreview) {
            URL.revokeObjectURL(coverImagePreview);
        }

        setCoverImagePreview(URL.createObjectURL(file));
    };

    useEffect(() => {
        return () => {
            if (audioPreview) {
                URL.revokeObjectURL(audioPreview);
            }

            if (coverImagePreview) {
                URL.revokeObjectURL(coverImagePreview);
            }
        };
    }, [audioPreview, coverImagePreview]);

    useEffect(() => {
        if (editingTrack) {
            reset({
                contentLanguage: editingTrack.contentLanguage || "en",
                title: editingTrack.title || editingTrack.titleEn || "",
                description: editingTrack.description || editingTrack.descriptionEn || "",
                trackNumber: editingTrack.trackNumber || 1,
                uploadType: editingTrack.uploadType || "simple_upload",
                status: editingTrack.status || "DRAFT",
                libraryAudioId: editingTrack.audioLibraryId || "",
                isExplicit: editingTrack.isExplicit || false
            });

            // Set existing cover image preview
            if (editingTrack?.coverImage) {
                setCoverImagePreview(getFullUrl(editingTrack.coverImage, 'album'));
            }

        } else {
            setCoverImagePreview(null);
        }
    }, [editingTrack, reset]);

    const validateAudioFile = async (file) => {
        if (!file) return false;

        // File type validation
        if (!ALLOWED_AUDIO_TYPES.includes(file.type)) {
            toast.error(
                t(
                    "albums.albumDetails.messages.invalid_audio_type",
                    "Only MP3, WAV, AAC audio files are allowed"
                )
            );
            return false;
        }

        // File size validation
        if (file.size > MAX_AUDIO_SIZE) {
            toast.error(
                t(
                    "albums.albumDetails.messages.audio_size_limit",
                    "Audio file size should not exceed 100MB"
                )
            );
            return false;
        }

        // Audio duration validation
        const duration = await getAudioDuration(file);

        if (duration < 5) {
            toast.error(
                t(
                    "albums.albumDetails.messages.audio_duration_min",
                    "Audio duration must be at least 5 seconds"
                )
            );
            return false;
        }

        if (duration > 60 * 60 * 3) {
            toast.error(
                t(
                    "albums.albumDetails.messages.audio_duration_max",
                    "Audio duration cannot exceed 3 hours"
                )
            );
            return false;
        }

        setAudioDuration(duration);

        return true;
    };

    const uploadAudioWithTus = (file) => {
        const token = sessionStorage.getItem("admin-token");

        return new Promise((resolve, reject) => {
            const upload = new tus.Upload(file, {
                endpoint: process.env.REACT_APP_TUS_ENDPOINT,
                retryDelays: [0, 3000, 5000, 10000],

                headers: {
                    Authorization: `Bearer ${token}`,
                },

                metadata: {
                    filename: file.name,
                    filetype: file.type,
                },

                onError: (error) => {
                    console.error("Upload failed:", error);
                    toast.error(t('album.albumDetails.messages.audio_upload_failed', "Audio upload failed"));
                    reject(error);
                },

                onProgress: (bytesUploaded, bytesTotal) => {
                    const percentage = Math.floor((bytesUploaded / bytesTotal) * 100);
                    console.log("Upload progress:", percentage);
                },

                onSuccess: () => {
                    console.log("Upload finished:", upload.url);

                    resolve(upload.url); // this is the file location
                },
            });

            upload.findPreviousUploads().then((previousUploads) => {
                if (previousUploads.length) {
                    upload.resumeFromPreviousUpload(previousUploads[0]);
                }

                upload.start();
            });
        });
    };

    const handleTrackSubmit = async (data, chosenActionType) => {
        const currentAction = chosenActionType || actionTypeRef.current || actionType;
        // try {
        //     console.log('submit');
        //     const formData = new FormData();

        //     const statusMap = {
        //         draft: "DRAFT",
        //         review: "PENDING_APPROVAL",
        //         publish: "PUBLISHED",
        //     };

        //     // Combine translations properly
        //     const translations = [
        //         { lang: "en", title: data.titleEn, description: data.descriptionEn, metaTitle: data.metaTitle, metaDescription: data.metaDescription, metaKeywords: data.metaKeywords || "" },
        //         { lang: "fr", title: data.titleFr, description: data.descriptionFr, metaTitle: data.metaTitle, metaDescription: data.metaDescription, metaKeywords: data.metaKeywords || "" }
        //     ];
        //     formData.append("translations", JSON.stringify(translations));

        //     formData.append("albumId", albumId);
        //     formData.append("trackNumber", data.trackNumber);
        //     formData.append("uploadType", data.uploadType);
        //     formData.append("status", statusMap[actionType]);

        //     if(uploadType === "library_upload"){
        //         formData.append("audioLibraryId", data.libraryAudioId || null);
        //     }

        //     let uploadedAudioUrl = null;
        //     console.log(data,'data');
        //     if (uploadType === "simple_upload" && data.audioFile instanceof File) {
        //         uploadedAudioUrl = await uploadAudioWithTus(data.audioFile);

        //         formData.append("audioUrl", uploadedAudioUrl);
        //         formData.append("file", data.audioFile);
        //     }

        //     if (data.coverImage && data.coverImage instanceof File) {
        //         formData.append("coverImage", data.coverImage);
        //     }

        //     if (editingTrack) {
        //         await dispatch(updateTrack({ id: editingTrack.id, formData })).unwrap();
        //         toast.success(t('album.albumDetails.messages.update_success',"Track updated successfully"));
        //     } else {
        //         await dispatch(createTrack(formData)).unwrap();
        //         toast.success(t('album.albumDetails.messages.create_success',"Track created successfully"));
        //     }

        //     handleClose();

        //     await dispatch(fetchTracks({
        //         albumId,
        //         page: paginationModel.page + 1,
        //         limit: paginationModel.pageSize,
        //         search: searchText,
        //     }));
        // } catch (err) {
        //     console.error("Album error:", err);
        //     toast.error(err?.message || t('something_went_wrong',"Something went wrong"));
        // }

        try {
            setUploading(true);

            const formData = new FormData();

            const statusMap = {
                draft: "DRAFT",
                review: "PENDING_APPROVAL",
                publish: "PUBLISHED",
            };

            formData.append("title", data.title?.trim());
            if (data.description) {
                formData.append("description", data.description?.trim());
            }

            formData.append("albumId", albumId);
            formData.append("trackNumber", data.trackNumber);
            formData.append("uploadType", data.uploadType);
            formData.append("status", statusMap[currentAction] || "DRAFT");
            formData.append("isExplicit", data.isExplicit);

            // Library Upload Validation
            if (
                data.uploadType === "library_upload" &&
                !data.libraryAudioId
            ) {
                toast.error(
                    t(
                        "albums.albumDetails.messages.audio_library_required",
                        "Please select audio library"
                    )
                );

                return;
            }

            if (data.uploadType === "library_upload") {
                formData.append(
                    "audioLibraryId",
                    data.libraryAudioId
                );
            }

            // Simple Upload Validation
            if (
                data.uploadType === "simple_upload" &&
                data.audioFile instanceof File
            ) {
                const isValid = await validateAudioFile(data.audioFile);

                if (!isValid) {
                    return;
                }

                const uploadedAudioUrl = await uploadAudioWithTus(
                    data.audioFile
                );

                formData.append("audioUrl", uploadedAudioUrl);
                formData.append("file", data.audioFile);
                formData.append("durationSeconds", Math.floor(audioDuration));
            }

            if (data.coverImage instanceof File) {
                formData.append("coverImage", data.coverImage);
            }

            if (editingTrack) {
                await dispatch(
                    updateTrack({
                        id: editingTrack.id,
                        formData,
                    })
                ).unwrap();

                toast.success(
                    t(
                        "album.albumDetails.messages.update_success",
                        "Track updated successfully"
                    )
                );
            } else {
                await dispatch(createTrack(formData)).unwrap();

                toast.success(
                    t(
                        "album.albumDetails.messages.create_success",
                        "Track created successfully"
                    )
                );
            }

            handleClose();

            await dispatch(
                fetchTracks({
                    albumId,
                    page: paginationModel.page + 1,
                    limit: paginationModel.pageSize,
                    search: searchText,
                })
            );
        } catch (err) {
            console.error("API Error:", err);

            toast.error(
                (typeof err === "string" ? err : err?.message) ||
                t("something_went_wrong", "Something went wrong")
            );
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
                {editingTrack ? t('albums.albumDetails.actions.edit_track', "Edit Track") : t('albums.albumDetails.actions.add_track', "Add New Track")}
                <IconButton onClick={handleClose} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}><Close /></IconButton>
            </DialogTitle>
            <form onSubmit={handleSubmit((data) => handleTrackSubmit(data, actionTypeRef.current || actionType))}>
                <DialogContent
                    dividers
                    sx={{
                        borderColor: 'rgba(230, 210, 249, 0.1)',
                        maxHeight: { xs: 'calc(100vh - 264px)', sm: 'calc(100vh - 214px)' },
                        padding: '24px 16px',
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(230, 210, 249, 0.3)',
                            borderRadius: '4px',
                            '&:hover': {
                                background: 'rgba(230, 210, 249, 0.5)',
                            },
                        },
                    }}
                >

                    <Grid container columnSpacing={2}>
                        {/* Title */}
                        <Grid item xs={12}>
                            <Controller
                                name="title"
                                control={control}
                                rules={{
                                    required: t(
                                        "albums.albumDetails.validations.title_required",
                                        "Title is required"
                                    ),
                                    maxLength: {
                                        value: 150,
                                        message: t(
                                            "albums.albumDetails.validations.title_max",
                                            "Title cannot exceed 150 characters"
                                        ),
                                    },
                                }}
                                render={({ field }) =>
                                    <TextField
                                        {...field}
                                        fullWidth
                                        error={!!errors.title}
                                        helperText={errors.title?.message}
                                        placeholder={t('albums.albumDetails.fields.title', 'Title')}
                                        sx={{
                                            mb: 2,
                                            '& .MuiOutlinedInput-input': {
                                                padding: '12px 20px',
                                                fontWeight: '400',
                                                color: '#fff',
                                            },
                                            '& input::placeholder': {
                                                color: 'rgba(255,255,255,0.6)',
                                                opacity: 1,
                                            },
                                        }}
                                    />}
                            />
                        </Grid>

                        {/* Description */}
                        <Grid item xs={12}>
                            <Controller
                                name="description"
                                control={control}
                                render={({ field }) =>
                                    <TextField
                                        {...field}
                                        fullWidth
                                        multiline
                                        rows={3}
                                        error={!!errors.description}
                                        helperText={errors.description?.message}
                                        placeholder={t('albums.albumDetails.fields.description', 'Description')}
                                        sx={{
                                            mb: 2,
                                            '& .MuiOutlinedInput-input': {
                                                padding: '0 10px',
                                                fontWeight: '400',
                                                color: '#fff',
                                                '&::-webkit-scrollbar': {
                                                    width: '4px',
                                                },
                                                '&::-webkit-scrollbar-track': {
                                                    background: 'rgba(255, 255, 255, 0.05)',
                                                    borderRadius: '4px',
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    background: 'rgba(230, 210, 249, 0.3)',
                                                    borderRadius: '4px',
                                                },
                                            },
                                            '& input::placeholder': {
                                                color: 'rgba(255,255,255,0.6)',
                                                opacity: 1,
                                            },
                                        }}
                                    />}
                            />
                        </Grid>

                        {/* Explicit Content */}
                        <Grid item xs={12}>
                            <Controller
                                name="isExplicit"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: '#ee3918',
                                                    },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: '#ee3918',
                                                    },
                                                }}
                                            />
                                        }
                                        label={t("albums.albumDetails.fields.isExplicit", "Explicit Content")}
                                        sx={{ color: '#fff', mb: 2 }}
                                    />
                                )}
                            />
                        </Grid>

                        {/* IMAGE FULL WIDTH */}
                        <Grid item xs={12}
                            sx={{
                                mb: 2
                            }}
                        >
                            <Controller
                                name="coverImage"
                                control={control}
                                rules={{
                                    required: !editingTrack
                                        ? t('albums.albumDetails.validations.cover_image_required', 'Cover image is required')
                                        : false
                                }}
                                sx={{
                                    mb: 2
                                }}
                                render={() => (
                                    <>
                                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: '400', fontSize: '14px' }}>
                                            {t('albums.albumDetails.fields.upload_cover_image', "Upload Cover Image")} <Typography component="span" sx={{ fontSize: '13px', color: '#ff4d4f', ml: 1 }}>{t("albums.fields.upload_only_square_img", "(Upload Only Square Image)")}</Typography>
                                        </Typography>
                                        <Box
                                            component="label"
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const file = e.dataTransfer.files[0];
                                                if (file) {
                                                    handleCoverImageChange({ target: { files: [file] } });
                                                }
                                            }}
                                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                            sx={{
                                                border: "1px dashed rgba(230, 210, 249, 0.2)",
                                                p: 3,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                cursor: "pointer",
                                                borderRadius: "16px",
                                            }}
                                        >
                                            <input hidden type="file" accept="image/png, image/jpg, image/jpeg, image/webp" onChange={handleCoverImageChange} />
                                            {coverImagePreview ? (
                                                <img src={coverImagePreview} width={150} alt="preview" />
                                            ) : (
                                                <>
                                                    <Box sx={{ textAlign: 'center', pointerEvents: 'none' }}>
                                                        <CloudUpload
                                                            sx={{
                                                                fontSize: 35,
                                                                color: 'white',
                                                                alignItems: "center"
                                                            }}
                                                        />
                                                        <Typography variant="body1" sx={{ fontSize: '16px', my: '5px' }}>
                                                            {t('audio_library.popup_titles.drag_drop_image_here', "Drag & Drop Image Here")}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: '13px', color: '#ff4d4f', textAlign: 'center', mb: '4px' }}>
                                                            {t('administrator.fields.upload_square_only', '(Upload Only Square Image)')}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: '14px', color: "rgba(255,255,255,0.6)" }}>{t('audio_library.popup_titles.click_to_browse_image', "or click to browse (Supported formats: JPG, PNG, WEBP)")}</Typography>
                                                    </Box>
                                                </>
                                            )}
                                        </Box>

                                        {errors.coverImage && (
                                            <Typography
                                                sx={{
                                                    color: "#d32f2f",
                                                    fontSize: "12px",
                                                    mt: 1,
                                                }}
                                            >
                                                {errors.coverImage.message}
                                            </Typography>
                                        )}
                                    </>
                                )}
                            />
                        </Grid>

                        {/* Track & Album Info */}
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="trackNumber"
                                control={control}
                                rules={{
                                    required: t("albums.albumDetails.validations.track_number_required", "Track Number is required"),
                                    min: { value: 1, message: t("albums.albumDetails.validations.track_number_min", "Track number must be greater than 0") },
                                    max: { value: 9999, message: t("albums.albumDetails.validations.track_number_max", "Track number is too large") },
                                }}
                                render={({ field }) =>
                                    <TextField
                                        {...field}
                                        fullWidth
                                        error={!!errors.trackNumber}
                                        helperText={errors.trackNumber?.message}
                                        placeholder={t('albums.albumDetails.fields.track_number', "Track Number")}
                                        type="number"
                                        min={1}
                                        sx={{
                                            mb: 2,
                                            '& .MuiOutlinedInput-input': {
                                                padding: '12px 20px',
                                                fontWeight: '400',
                                                color: '#fff',
                                            },
                                            '& input::placeholder': {
                                                color: 'rgba(255,255,255,0.6)',
                                                opacity: 1,
                                            },
                                        }}
                                    />}
                            />
                        </Grid>

                        {/* Upload Type Selector */}
                        <Grid item xs={12} md={6}>
                            <Controller name="uploadType" control={control} rules={{ required: t('albums.albumDetails.validations.upload_type_required', "Please select Upload type") }} render={({ field }) => (
                                <FormControl fullWidth error={!!errors.uploadType}>
                                    <Select
                                        {...field}
                                        displayEmpty
                                        sx={{
                                            color: "#fff",
                                            fontWeight: 400,
                                            mb: 2,
                                            "& .MuiSelect-select": {
                                                padding: "12px 20px",
                                            },
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                border: "1px solid rgba(230, 210, 249, 0.1) !important",
                                            },
                                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                                border: "1px solid rgba(230, 210, 249, 0.1)",
                                            },
                                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                border: "1px solid rgba(230, 210, 249, 0.1)",
                                            },
                                            "& .MuiSvgIcon-root": {
                                                color: "#fff",
                                            },
                                        }}
                                    >
                                        <MenuItem value="simple_upload">{t('albums.albumDetails.simple_upload', "Simple Upload (Manual)")}</MenuItem>
                                        <MenuItem value="library_upload">{t('albums.albumDetails.library_upload', "Audio Library Upload")}</MenuItem>
                                    </Select>
                                    {errors.uploadType && <FormHelperText>{errors.uploadType.message}</FormHelperText>}
                                </FormControl>
                            )} />
                        </Grid>

                        {/* Conditional Logic: Library Dropdown */}
                        {uploadType === "library_upload" && (
                            <Grid item xs={12} md={6}>
                                <Controller name="libraryAudioId" control={control}
                                    rules={{
                                        required: uploadType === "library_upload" && !editingTrack
                                            ? t('albums.albumDetails.validations.audio_library_required', "Please select Audio Library")
                                            : false
                                    }}
                                    render={({ field }) => (
                                        <FormControl fullWidth error={!!errors.libraryAudioId}>
                                            <Autocomplete
                                                options={audios}
                                                getOptionLabel={(option) => option.originalName}
                                                onChange={(e, value) => setValue("libraryAudioId", value?.id)}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        error={!!errors.libraryAudioId}
                                                        placeholder={t('albums.albumDetails.fields.select_audio_library', "Select Audio Library")}
                                                        sx={{
                                                            '& .MuiOutlinedInput-input': {
                                                                padding: '12px 20px',
                                                                fontWeight: '400',
                                                                color: '#fff',
                                                            },
                                                            '& input::placeholder': {
                                                                color: 'rgba(255,255,255,0.6)',
                                                                opacity: 1,
                                                            },
                                                        }}
                                                    />
                                                )}
                                                sx={{
                                                    "& .MuiAutocomplete-clearIndicator svg": {
                                                        fill: "white"
                                                    },
                                                    "& .MuiOutlinedInput-root": {
                                                        background: "transparent",
                                                        color: "#fff",
                                                        paddingTop: '5px',
                                                        paddingBottom: '4px',
                                                        paddingLeft: '16px',
                                                        "& fieldset": {
                                                            borderColor: "#444",
                                                        },

                                                        "&:hover fieldset": {
                                                            borderColor: "#777",
                                                        },

                                                        "&.Mui-focused fieldset": {
                                                            borderColor: "#00bcd4",
                                                        },
                                                    },

                                                    "& .MuiInputLabel-root": {
                                                        color: "#bbb",
                                                    },

                                                    "& .MuiInputLabel-root.Mui-focused": {
                                                        color: "#00bcd4",
                                                    },

                                                    "& .MuiAutocomplete-popupIndicator": {
                                                        color: "#bbb",
                                                    },

                                                    "& .MuiAutocomplete-tag": {
                                                        background: "transparent",
                                                        color: "#fff",
                                                    },
                                                }}
                                                slotProps={{
                                                    popper: {
                                                        sx: {
                                                            "& .MuiAutocomplete-paper": {
                                                                backgroundColor: "#1e1e1e",
                                                                color: "#fff",
                                                                borderRadius: "10px",
                                                                border: "1px solid #333",
                                                                mt: 1,
                                                            },
                                                            "& .MuiAutocomplete-listbox": {
                                                                padding: "6px",
                                                                '&::-webkit-scrollbar': {
                                                                    width: '8px',
                                                                },
                                                                '&::-webkit-scrollbar-track': {
                                                                    background: 'rgba(255, 255, 255, 0.05)',
                                                                    borderRadius: '4px',
                                                                },
                                                                '&::-webkit-scrollbar-thumb': {
                                                                    background: 'rgba(230, 210, 249, 0.3)',
                                                                    borderRadius: '4px',
                                                                    '&:hover': {
                                                                        background: 'rgba(230, 210, 249, 0.5)',
                                                                    },
                                                                },
                                                            },
                                                            "& .MuiAutocomplete-option": {
                                                                fontSize: "14px",
                                                                borderRadius: "6px",
                                                                padding: "10px 12px",
                                                            },
                                                            "& .MuiAutocomplete-option:hover": {
                                                                backgroundColor: "#2c2c2c",
                                                            },
                                                            "& .MuiAutocomplete-option.Mui-focused": {
                                                                backgroundColor: "#2c2c2c",
                                                            },
                                                            "& .MuiAutocomplete-option[aria-selected='true']": {
                                                                backgroundColor: "#00bcd4",
                                                                color: "#000",
                                                            },
                                                            "& .MuiAutocomplete-noOptions": {
                                                                color: "#fff",
                                                                textAlign: "center"
                                                            },
                                                            "& .MuiListSubheader-sticky": {
                                                                backgroundColor: "#4a484e",
                                                                color: "#fff"
                                                            }
                                                        },
                                                    },
                                                }}
                                            />
                                            {errors.libraryAudioId && <FormHelperText>{errors.libraryAudioId.message}</FormHelperText>}
                                        </FormControl>
                                    )} />
                            </Grid>
                        )}

                        {/* Conditional Logic: Simple Drag & Drop */}
                        {uploadType === "simple_upload" && (
                            <Grid item xs={12}>
                                <Controller
                                    name="audioFile"
                                    control={control}
                                    rules={{
                                        required: uploadType === "simple_upload" && !editingTrack
                                            ? t('albums.albumDetails.validations.upload_audio_required', "Please upload audio")
                                            : false
                                    }}
                                    render={({ field }) => (
                                        <>
                                            <Paper
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const file = e.dataTransfer.files[0];

                                                    handleAudioSelection(file, field);
                                                }}
                                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                                                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
                                                onClick={() => fileInputRef.current.click()}
                                                sx={{
                                                    border: errors.audioFile ? "1px dashed #d32f2f" : "1px dashed rgba(230, 210, 249, 0.1)",
                                                    borderRadius: "100px",
                                                    minHeight: 120,
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    cursor: "pointer",
                                                    transition: "0.3s",
                                                    padding: '20px'
                                                }}
                                            >
                                                <Box sx={{ pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                    <CloudUpload
                                                        sx={{
                                                            fontSize: 35,
                                                            color: 'white',
                                                        }}
                                                    />
                                                    <Typography variant="body1" sx={{ fontSize: '16px', my: '5px' }}>{t('audio_library.popup_titles.drag_drop_audio_here', "Drag & Drop Audio Here")}</Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '14px', color: "rgba(255,255,255,0.6)" }}>{t('audio_library.popup_titles.click_to_browse', "or click to browse (Supported formats: MP3)")}</Typography>
                                                    {field.value && <Typography mt={2} sx={{ fontSize: '16px', color: 'white' }}>{t('selected', "Selected")}: {field.value.name}</Typography>}
                                                </Box>
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    hidden
                                                    accept=".mp3,audio/mpeg"
                                                    onChange={async (e) => {
                                                        const file = e.target.files[0];

                                                        await handleAudioSelection(file, field);

                                                        e.target.value = null;
                                                    }}
                                                />
                                            </Paper>
                                            {errors.audioFile && (
                                                <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 1, ml: 2 }}>
                                                    {errors.audioFile.message}
                                                </Typography>
                                            )}
                                        </>
                                    )} />

                                {audioPreview && (
                                    <Box mt={2} width="100%">
                                        <audio controls style={{ width: "100%" }}>
                                            <source src={audioPreview} />
                                            {t('browser_not_support', "Your browser does not support audio preview.")}
                                        </audio>
                                    </Box>
                                )}
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', gap: '6px' }}>
                    <Button
                        onClick={handleClose}
                        disabled={uploading}
                        sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}
                    >
                        {t('cancel', "Cancel")}
                    </Button>

                    {/* Save as Draft */}
                    <Button
                        type="submit"
                        onClick={() => { setActionType("draft"); actionTypeRef.current = "draft"; }}
                        disabled={uploading}
                        sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}
                    >
                        {uploading && (actionType === "draft" || actionTypeRef.current === "draft") && <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />}
                        {t('albums.albumDetails.actions.save_as_draft', "Save as Draft")}
                    </Button>

                    {((user.roleName === "Podcaster" || user.roleId === 3) && !user?.isVerified) && (
                        <>
                            {/* Request Review */}
                            <Button
                                variant="outlined"
                                type="submit"
                                disabled={uploading}
                                onClick={() => { setActionType("review"); actionTypeRef.current = "review"; }}
                                sx={{
                                    border: "1px solid #FD56C9",
                                    color: "#FD56C9",
                                    borderRadius: "999px",
                                    fontSize: "15px",
                                    textTransform: "capitalize",
                                    minWidth: { xs: "100%", sm: "150px" },
                                }}
                            >
                                {uploading && (actionType === "review" || actionTypeRef.current === "review") && <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />}
                                {t('albums.albumDetails.actions.save_as_request_review', "Save and Request for Review")}
                            </Button>
                        </>
                    )}
                    {/* Publish Now */}
                    {(!(user.roleName === "Podcaster" || user.roleId === 3) || user?.isVerified) && (
                        <Button
                            variant="contained"
                            type="submit"
                            disabled={uploading}
                            onClick={() => { setActionType("publish"); actionTypeRef.current = "publish"; }}
                            sx={{
                                py: 1.5,
                                borderRadius: '999px',
                                fontWeight: '400',
                                fontSize: '15px',
                                lineHeight: '20px',
                                textTransform: 'none',
                                background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                                textTransform: 'capitalize',
                                minWidth: { xs: '100%', sm: '150px' },
                                marginLeft: '10px',
                            }}
                        >
                            {uploading && (actionType === "publish" || actionTypeRef.current === "publish") && <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />}
                            {t('albums.albumDetails.actions.save_and_publish_now', "Save and Publish Now")}
                        </Button>
                    )}

                </DialogActions>
            </form>
        </Dialog>
    );
};

/**
 * PAGE COMPONENT: ALBUM MANAGEMENT DETAILS
 */
function AlbumManagementDetails() {
    const { t, i18n } = useTranslation();

    const { albumSlug } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const selectedAlbum = useSelector((state) => state.albums.selectedAlbum);

    const albumLoading = useSelector((state) => state.albums.loading);

    const { tracks = [], loading, pagination, nextTrackNumber } = useSelector((state) => state.tracks || {});
    const [togglingTrackId, setTogglingTrackId] = useState(null);

    const [searchText, setSearchText] = useState("");
    const [selectedTrack, setSelectedTrack] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
    const [scheduleSelectedTrack, setScheduleSelectedTrack] = useState(null);

    const [publishTrackDialogOpen, setPublishTrackDialogOpen] = useState(false);
    const [publishing, setPublishing] = useState(false);

    const { user } = useSelector((state) => state.auth || state.AuthReducer || {});
    const isCurrentAlbumLoaded = selectedAlbum?.slug === albumSlug;

    const [trackModalOpen, setTrackModalOpen] = useState(false);

    const [viewModalOpen, setViewModalOpen] = useState(false);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const [scheduleConfirmDialog, setScheduleConfirmDialog] = useState({
        open: false,
        id: null
    });

    // For Table Thead Width
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
    const isLarge = useMediaQuery(theme.breakpoints.up('xl'));

    useEffect(() => {
        if (albumSlug) {
            dispatch(fetchAlbumBySlug(albumSlug));
        }
    }, [dispatch, albumSlug]);

    // Fetch tracks on mount
    useEffect(() => {
        if (!selectedAlbum?.id) return;

        dispatch(
            fetchTracks({
                albumId: selectedAlbum.id,
                page: paginationModel.page + 1,
                limit: paginationModel.pageSize,
                search: searchText,
            })
        );
    }, [selectedAlbum?.id, paginationModel, searchText]);

    const handleView = async (row) => {
        setSelectedTrack(null);
        setViewModalOpen(true);
        try {
            const res = await dispatch(fetchTrackById(row.id)).unwrap();
            setSelectedTrack(res);
        } catch (err) {
            console.error("Failed to fetch track details:", err);
        }
    };

    const handlePublishTrackRequest = (row) => {
        setSelectedTrack(row);
        setPublishTrackDialogOpen(true);
    };

    const handlePublishTrackRequestConfirm = async () => {
        setPublishing(true);
        try {
            const response = await dispatch(publishTrackRequest({ id: selectedTrack.id })).unwrap();
            toast.success(response.message);
            dispatch(
                fetchTracks({
                    albumId: selectedAlbum.id,
                    page: paginationModel.page + 1,
                    limit: paginationModel.pageSize,
                    search: searchText,
                })
            );
        } catch (err) {
            toast.error(err);
        } finally {
            setPublishTrackDialogOpen(false);
            setSelectedTrack(null);
            setPublishing(false);
        }
    };

    const handleEdit = async (row) => {
        const res = await dispatch(fetchTrackById(row.id)).unwrap();
        setSelectedTrack(res);
        setTrackModalOpen(true);
    };

    const handleDeleteClick = (row) => {
        setSelectedRow(row);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await dispatch(deleteTrack({ id: selectedRow.id })).unwrap();
            toast.success(t('albums.albumDetails.messages.track_deleted', 'Track deleted'));
            dispatch(fetchTracks({
                albumId: selectedRow.albumId,
                page: paginationModel.page + 1,
                limit: paginationModel.pageSize,
                search: searchText,
            }));
        } catch {
            toast.error(t('albums.albumDetails.messages.delete_failed', 'Delete failed'));
        } finally {
            setDeleteDialogOpen(false);
        }
    };

    const handleExplicitToggle = async (rowId, newStatus) => {
        setTogglingTrackId(rowId);
        try {
            const row = await dispatch(fetchTrackById(rowId)).unwrap();
            const formData = new FormData();
            formData.append("albumId", row.albumId || selectedAlbum?.id);
            formData.append("trackNumber", row.trackNumber || "");
            formData.append("title", row.title || row.titleEn || "");
            formData.append("description", row.description || row.descriptionEn || "");
            formData.append("status", row.status || "DRAFT");
            formData.append("isExplicit", newStatus);
            if (row.uploadType) formData.append("uploadType", row.uploadType);
            if (row.audioLibraryId) formData.append("audioLibraryId", row.audioLibraryId);

            await dispatch(updateTrack({ id: rowId, formData })).unwrap();
            dispatch(fetchTracks({
                albumId: selectedAlbum?.id,
                page: paginationModel.page + 1,
                limit: paginationModel.pageSize,
                search: searchText,
            }));
            toast.success(t('albums.albumDetails.messages.explicit_status_updated', "Explicit status updated successfully"));
        } catch (error) {
            toast.error(error?.message || t('something_went_wrong', "Something went wrong"));
        } finally {
            setTogglingTrackId(null);
        }
    };

    const formatDuration = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${s < 10 ? "0" : ""}${s}`;
    };

    const trackColumns = [
        {
            field: 'sno',
            headerName: t('albums.albumDetails.fields.sno', 'S.No'),
            width: 80,
            sortable: false,
            renderCell: (params) => {
                const allRows = params.api.getAllRowIds();
                const currentRowIndex = allRows.indexOf(params.id);
                const visibleRowIndex = currentRowIndex % paginationModel.pageSize;
                return paginationModel.page * paginationModel.pageSize + visibleRowIndex + 1;
            }
        },
        {
            field: "coverImage",
            headerName: "",
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            width: 60,
            sortable: false,
            renderCell: (params) => (
                <Image src={params.value} style={{ width: 60, height: 60 }} />
            ),
        },
        {
            field: "title",
            headerName: t("albums.albumDetails.fields.title", "Title"),
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {params.row.isExplicit && (
                        <Tooltip title={t("albums.albumDetails.fields.explicit", "Explicit Content")} arrow placement="top">
                            <Box sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                color: 'rgba(255, 255, 255, 0.7)',
                                borderRadius: '3px',
                                padding: '0 4px',
                                minWidth: '16px',
                                height: '16px',
                                fontSize: '10px',
                                fontWeight: '700',
                                mr: 1,
                                cursor: 'default'
                            }}>
                                E
                            </Box>
                        </Tooltip>
                    )}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={params.value}>{params.value}</span>
                </Box>
            )
        },
        {
            field: 'audio_file_name',
            headerName: t('albums.albumDetails.fields.file_name', 'File Name'),
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            renderCell: (params) => params.value
        },
        {
            field: 'durationSeconds',
            headerName: t('albums.albumDetails.fields.duration', 'Duration'),
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            renderCell: (params) => formatDuration(params.value)
        },
        {
            field: 'status',
            headerName: t('albums.albumDetails.fields.status', 'Status'),
            display: 'flex',
            alignItems: 'center',
            ...(isMobile && { width: 150 }),
            ...(isLarge && { flex: 1 }),
            renderCell: (params) => {
                const scheduledAt = params.row.scheduledAt;

                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '100%' }}>
                        <Typography sx={{ color: params.value === 'Published' ? '#03c11c' : '#e3b220', fontWeight: 'bold', fontSize: '13px', lineHeight: 1.3 }}>
                            {params.value}
                        </Typography>
                        {String(params.row.originalStatus || '').toUpperCase() === 'SCHEDULED' && scheduledAt && (
                            <Typography sx={{ color: '#bdbdbd', fontSize: '11px', lineHeight: 1.3, mt: 0.25 }}>
                                ({formatUTCDateForDataGrid(scheduledAt)})
                            </Typography>
                        )}
                    </Box>
                );
            }
        },

        {
            field: 'actions',
            width: 250,
            minWidth: 220,
            headerName: t('albums.albumDetails.fields.actions', 'Actions'),
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: '6px', flexWrap: 'nowrap' }}>
                    <IconButton onClick={() => handleView(params.row)}
                        sx={{
                            backgroundColor: 'rgba(227, 214, 245, 0.2) !important',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid rgba(227, 214, 245, 0.1)',
                            padding: '0'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="13" viewBox="0 0 18 13" fill="none">
                            <path d="M17.415 4.66071C15.5893 1.74214 12.4457 0 9 0C5.55429 0 2.41071 1.74214 0.585 4.66071L0.437143 4.89857C0.154286 5.36143 0 5.88857 0 6.42857C0 6.96857 0.154286 7.49571 0.437143 7.95857L0.585 8.19643C2.41071 11.115 5.55429 12.8571 9 12.8571C12.4457 12.8571 15.5893 11.115 17.415 8.19643L17.5629 7.95857C17.8457 7.49571 18 6.96857 18 6.42857C18 5.88857 17.8457 5.36143 17.5629 4.89857L17.415 4.66071ZM16.47 7.27714L16.3221 7.515C14.7343 10.0543 11.9957 11.5714 9 11.5714C6.00429 11.5714 3.26571 10.0543 1.67786 7.515L1.53 7.27714C1.36929 7.02 1.28571 6.73071 1.28571 6.42857C1.28571 6.12643 1.36929 5.83714 1.53 5.58L1.67786 5.34214C3.26571 2.80286 6.00429 1.28571 9 1.28571C11.9957 1.28571 14.7343 2.80286 16.3221 5.34214L16.47 5.58C16.6307 5.83714 16.7143 6.12643 16.7143 6.42857C16.7143 6.73071 16.6307 7.02 16.47 7.27714Z" fill="#ffffff" />
                            <path d="M8.99972 2.57129C6.87186 2.57129 5.14258 4.30057 5.14258 6.42843C5.14258 8.55629 6.87186 10.2856 8.99972 10.2856C11.1276 10.2856 12.8569 8.55629 12.8569 6.42843C12.8569 4.30057 11.1276 2.57129 8.99972 2.57129ZM8.99972 8.99986C7.57901 8.99986 6.42829 7.84915 6.42829 6.42843C6.42829 5.00772 7.57901 3.857 8.99972 3.857C10.4204 3.857 11.5711 5.00772 11.5711 6.42843C11.5711 7.84915 10.4204 8.99986 8.99972 8.99986Z" fill="#ffffff" />
                        </svg>
                    </IconButton>
                    <IconButton onClick={() => handleEdit(params.row)}
                        sx={{
                            backgroundColor: 'rgba(227, 214, 245, 0.2) !important',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid rgba(227, 214, 245, 0.1)',
                            padding: '0'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M15.531 3.84226C13.9473 5.42594 10.2302 9.14231 9.3237 10.0459C9.12779 10.2411 8.89463 10.3952 8.64435 10.4958C7.91732 10.7975 6.14371 11.4932 5.62228 11.5283C5.31165 11.5491 5.00699 11.4344 4.7865 11.2139C4.566 10.9934 4.45129 10.6887 4.47215 10.3781C4.50716 9.85668 5.2029 8.08453 5.49937 7.36048C5.60291 7.10348 5.7571 6.87032 5.95301 6.67516L12.1581 0.469295C12.4582 0.169095 12.8665 0 13.291 0C13.7156 0 14.1239 0.169095 14.424 0.469295L15.531 1.57623C15.8312 1.87643 16.0003 2.28465 16.0003 2.70925C16.0003 3.13384 15.8312 3.54206 15.531 3.84226ZM5.59174 10.4087C6.10125 10.3446 7.59031 9.72334 8.21603 9.46336C8.21826 9.46262 8.2205 9.46187 8.22199 9.46113C8.33894 9.4142 8.44471 9.34418 8.53336 9.25553L8.5341 9.25479C9.44214 8.35121 13.1577 4.63559 14.7406 3.0519C14.8315 2.96103 14.8829 2.83811 14.8829 2.70925C14.8829 2.58038 14.8315 2.45746 14.7406 2.36659L13.6337 1.25965C13.5428 1.16877 13.4199 1.11737 13.291 1.11737C13.1622 1.11737 13.0393 1.16877 12.9484 1.25965L6.74261 7.46551C6.65322 7.55415 6.5832 7.66068 6.53627 7.77763L6.53478 7.78061C6.27779 8.40782 5.6558 9.89839 5.59174 10.4087Z" fill="white" />
                            <path fillRule="evenodd" clipRule="evenodd" d="M14.0542 4.52797C14.2717 4.74623 14.2717 5.10006 14.0542 5.31832C13.8359 5.53658 13.4821 5.53658 13.2638 5.31832L10.6813 2.73571C10.463 2.51745 10.463 2.16362 10.6813 1.94536C10.8995 1.72785 11.2534 1.72785 11.4716 1.94536L14.0542 4.52797Z" fill="white" />
                            <path fillRule="evenodd" clipRule="evenodd" d="M9.49485 9.08712C9.71311 9.30464 9.71311 9.65922 9.49485 9.87673C9.27734 10.095 8.92277 10.095 8.70525 9.87673L6.12268 7.29412C5.90442 7.07661 5.90442 6.72203 6.12268 6.50451C6.34019 6.28626 6.69476 6.28626 6.91227 6.50451L9.49485 9.08712Z" fill="white" />
                            <path fillRule="evenodd" clipRule="evenodd" d="M5.40054 1.47412C5.70893 1.47412 5.95922 1.72441 5.95922 2.03281C5.95922 2.3412 5.70893 2.59149 5.40054 2.59149H2.79339C1.86747 2.59149 1.11735 3.34162 1.11735 4.26754V13.2065C1.11735 13.6512 1.2939 14.0773 1.60824 14.3916C1.92259 14.706 2.34868 14.8825 2.79339 14.8825H12.4771C12.9218 14.8825 13.3479 14.706 13.6623 14.3916C13.9766 14.0773 14.1532 13.6512 14.1532 13.2065V10.5993C14.1532 10.2909 14.4034 10.0406 14.7118 10.0406C15.0202 10.0406 15.2705 10.2909 15.2705 10.5993V13.2065C15.2705 13.9477 14.9763 14.6576 14.4526 15.182C13.9282 15.7057 13.2183 15.9999 12.4771 15.9999H2.79339C2.05221 15.9999 1.34231 15.7057 0.817903 15.182C0.294237 14.6576 0 13.9477 0 13.2065V4.26754C0 2.72483 1.25069 1.47412 2.79339 1.47412H5.40054Z" fill="white" />
                        </svg>
                    </IconButton>
                    <IconButton onClick={() => handleDeleteClick(params.row)}
                        sx={{
                            backgroundColor: 'rgba(227, 214, 245, 0.2) !important',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid rgba(227, 214, 245, 0.1)',
                            padding: '0'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
                            <path d="M7.21951 1.17073C6.4558 1.17073 5.80457 1.65856 5.56322 2.34141C5.45548 2.64622 5.12105 2.80599 4.81623 2.69825C4.51142 2.59051 4.35166 2.25608 4.4594 1.95127C4.86091 0.81529 5.94427 0 7.21951 0C8.49483 0 9.57815 0.81529 9.97971 1.95127C10.0874 2.25608 9.92765 2.59051 9.62287 2.69825C9.31801 2.80599 8.98357 2.64622 8.87586 2.34141C8.63454 1.65856 7.98322 1.17073 7.21951 1.17073Z" fill="white" />
                            <path d="M0 3.70744C0 3.38415 0.26208 3.12207 0.585366 3.12207H13.8537C14.177 3.12207 14.4391 3.38415 14.4391 3.70744C14.4391 4.03072 14.177 4.2928 13.8537 4.2928H0.585366C0.26208 4.2928 0 4.03072 0 3.70744Z" fill="white" />
                            <path d="M2.47023 5.61969C2.44873 5.29712 2.1698 5.05306 1.84723 5.07456C1.52466 5.09606 1.28059 5.37499 1.3021 5.69757L1.66382 11.1233C1.73054 12.1245 1.78444 12.9332 1.91086 13.5678C2.04229 14.2275 2.26584 14.7786 2.72758 15.2106C3.1893 15.6426 3.75403 15.8289 4.42107 15.9162C5.06266 16.0001 5.87318 16.0001 6.87657 16.0001H7.56246C8.56586 16.0001 9.37632 16.0001 10.018 15.9162C10.685 15.8289 11.2497 15.6426 11.7115 15.2106C12.1732 14.7786 12.3967 14.2275 12.5282 13.5678C12.6545 12.9332 12.7085 12.1245 12.7752 11.1233L13.1369 5.69757C13.1584 5.37499 12.9143 5.09606 12.5918 5.07456C12.2692 5.05306 11.9902 5.29712 11.9688 5.61969L11.6098 11.0043C11.5397 12.0564 11.4896 12.7883 11.38 13.339C11.2735 13.8733 11.125 14.156 10.9116 14.3557C10.6982 14.5553 10.4062 14.6847 9.86607 14.7554C9.30927 14.8282 8.57562 14.8294 7.52133 14.8294H6.9177C5.86342 14.8294 5.12972 14.8282 4.57292 14.7554C4.03282 14.6847 3.7408 14.5553 3.5274 14.3557C3.314 14.156 3.16545 13.8733 3.05903 13.339C2.94932 12.7883 2.89935 12.0564 2.82922 11.0043L2.47023 5.61969Z" fill="white" />
                            <path d="M5.20978 7.02736C5.53147 6.9952 5.81828 7.2299 5.85051 7.55161L6.24076 11.4541C6.27291 11.7757 6.03822 12.0626 5.71651 12.0948C5.39484 12.1269 5.10797 11.8922 5.07581 11.5705L4.68557 7.66806C4.65339 7.34642 4.8881 7.05952 5.20978 7.02736Z" fill="white" />
                            <path d="M9.75346 7.66806C9.78561 7.34642 9.55092 7.05952 9.22921 7.02736C8.90757 6.9952 8.62066 7.2299 8.5885 7.55161L8.19826 11.4541C8.1661 11.7757 8.4008 12.0626 8.72251 12.0948C9.04415 12.1269 9.33098 11.8922 9.36321 11.5705L9.75346 7.66806Z" fill="white" />
                        </svg>
                    </IconButton>
                    {(params.row?.originalStatus != "PUBLISHED" && (user?.roleName === "Podcaster" || user?.roleId === 3 || user?.roleName === "Super Admin" || user?.roleId === 1)) && (
                        <IconButton
                            size="small"
                            title={t("albums.albumDetails.actions.publish_request", "Publish Request")}
                            onClick={() => handlePublishTrackRequest(params.row)}
                            sx={{ color: '#ffffff', backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
                        >
                            <Publish fontSize="small" />
                        </IconButton>
                    )}

                    {(params.row?.originalStatus != "PUBLISHED" && params.row?.originalStatus != "SCHEDULED" && (user?.roleName === "Podcaster" || user?.roleId === 3 || user?.roleName === "Super Admin" || user?.roleId === 1)) && (
                        <IconButton
                            size="small"
                            title={t("albums.albumDetails.actions.schedule_request", "Schedule Request")}
                            onClick={() => {
                                setScheduleConfirmDialog({ open: true, id: params.row.id });
                                setScheduleSelectedTrack(params.row);
                            }}
                            sx={{ color: '#ffffff', backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
                        >
                            <Schedule fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            )
        }
    ];

    const computedNextTrackNumber = Math.max(
        Number(nextTrackNumber) || 1,
        (Number(pagination?.total) || 0) + 1
    );

    const trackRows = useMemo(() => {
        if (!Array.isArray(tracks) || tracks.length === 0) {
            return [];
        }

        return tracks.map((track, index) => {
            if (!track || !track.id) {
                console.warn('Invalid track at index', index, track);
                return null;
            }

            return {
                id: track.id,
                backendId: track.id,
                slug: track.slug,
                sno: index + 1,
                title: track.title || track.titleEn || "",
                status: formatEnumLabel(track.status),
                originalStatus: track.status,
                scheduledAt: track.scheduledAt || null,
                durationSeconds: track.durationSeconds || 0,
                createdAt: track.createdAt || null,
                audio_file_name: track?.audioLibrary?.originalName || null,
                coverImage: getFullUrl(track?.coverImage, 'track'),
                isExplicit: track.isExplicit || false,
            };
        }).filter(Boolean); // Remove any null entries
    }, [tracks]);

    if (albumLoading) {
        return (
            <Box p={5} display="flex" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: '36px 24px 24px' }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >

                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px', mb: '24px', }} className='main_title'>{t("albums.albumDetails.title", "Album Details")}</Typography>

                <Button
                    startIcon={<ArrowBack />}
                    sx={{
                        fontWeight: 700,
                        color: "white",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                    }}
                    onClick={() => navigate("/albums")}
                >
                    {t("albums.albumDetails.actions.back_to_albums", "Back to Albums")}
                </Button>
            </Box>

            <Grid container spacing={3} sx={{ mb: 6 }}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: '10px', bgcolor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)' }}>
                        <CardContent sx={{ padding: '16px !important' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontSize: '16px', fontWeight: '500', color: 'white' }}>{t("albums.albumDetails.fields.cover_image", "Cover Image")}</Typography>
                                {selectedAlbum?.isExplicit && (
                                    <Tooltip title={t("albums.albumDetails.fields.explicit", "Explicit Content")} arrow placement="top">
                                        <Box sx={{ padding: '2px 8px', borderRadius: '4px', backgroundColor: 'rgba(255, 255, 255, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255, 255, 255, 0.8)', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', cursor: 'default' }}>
                                            Explicit
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>
                            <Box sx={{ width: '100%', height: '220px', bgcolor: '#222', borderRadius: '10px', overflow: 'hidden' }}>
                                {!isCurrentAlbumLoaded ? (
                                    <Skeleton
                                        variant="rectangular"
                                        animation="wave"
                                        width="100%"
                                        height="100%"
                                        sx={{ bgcolor: 'rgba(255,255,255,0.12)' }}
                                    />
                                ) : (
                                    <img
                                        src={getFullUrl(selectedAlbum?.coverImage?.cdnUrl, "album")}
                                        alt="Cover"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: '10px', bgcolor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', minHeight: '100%' }}>
                        <CardContent sx={{ padding: '8px 16px !important' }}>
                            <Typography variant="subtitle1" sx={{ mb: 0.3, fontSize: '16px', fontWeight: '500', color: 'white' }}>{t("albums.albumDetails.basic_information", "Basic Information")}</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ fontSize: '16px', color: 'white', fontWeight: '500' }}>{t('albums.fields.type', 'Type')}</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: 'white', fontWeight: '400' }}>{selectedAlbum?.type || ''}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ fontSize: '16px', color: 'white', fontWeight: '500' }}>{t('albums.fields.status', 'Status')}</Typography>
                                    <Typography variant="body2" sx={{ color: selectedAlbum?.status === "PUBLISHED" ? "#03c11c" : "#e3b220", fontWeight: 'bold', fontSize: '13px' }}>{formatEnumLabel(selectedAlbum?.status) || "Draft"}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ fontSize: '16px', color: 'white', fontWeight: '500' }}>{t('albums.fields.title', 'Title')}</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: 'white', fontWeight: '400' }}>{selectedAlbum?.title || ''}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ fontSize: '16px', color: 'white', fontWeight: '500' }}>{t('albums.fields.explicit_content', 'Explicit Content')}</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: 'white', fontWeight: '400', textTransform: 'capitalize' }}>
                                        {selectedAlbum?.isExplicit ? t("yes", "Yes") : t("no", "No")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ fontSize: '16px', color: 'white', fontWeight: '500' }}>{t('albums.fields.description', 'Description')}</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: 'white', fontWeight: '400' }}>{selectedAlbum?.description || ''}</Typography>
                                </Grid>
                                {String(selectedAlbum?.status || '').toUpperCase() === 'SCHEDULED' && <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ fontSize: '16px', color: 'white', fontWeight: '500' }}>{t('albums.fields.scheduledAt', 'Scheduled At')}</Typography>
                                    <Typography variant="body1" sx={{ fontSize: '14px', color: 'white', fontWeight: '400' }}>
                                        {selectedAlbum?.scheduledAt || selectedAlbum?.publishAt
                                            ? formatUTCDate(selectedAlbum.scheduledAt || selectedAlbum.publishAt)
                                            : 'N/A'}
                                    </Typography>
                                </Grid>}
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ fontSize: '16px', color: 'white', fontWeight: '500' }}>{t('albums.fields.categories', 'Categories')}</Typography>
                                    <Typography variant="body2" sx={{ fontSize: '14px', color: 'white', fontWeight: '400' }}>
                                        {selectedAlbum?.categories && selectedAlbum.categories.length > 0
                                            ? selectedAlbum.categories.map(cat => cat.name).join(', ')
                                            : "N/A"}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ fontSize: '16px', color: 'white', fontWeight: '500' }}>{t('albums.fields.podcaster', 'Podcaster')}</Typography>
                                    <Typography variant="body1" sx={{ fontSize: '14px', color: 'white', fontWeight: '400' }}>{selectedAlbum?.podcasterName || "N/A"}</Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px', }} className='main_title'>{t('albums.albumDetails.album_tracks', 'Album Tracks')}</Typography>
                <Box display="flex" gap={2}>
                    <TextField
                        size="small"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder={t('search', 'Search')}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '999px',
                                backgroundColor: 'rgba(230, 210, 249, 0.2)',
                                color: '#fff',
                                height: '38px',
                                '& fieldset': {
                                    borderColor: 'rgba(230, 210, 249, 0.1)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(230, 210, 249, 0.1)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(230, 210, 249, 0.1)',
                                    borderWidth: '1px',
                                },
                            },
                            '& .MuiOutlinedInput-input': {
                                padding: '10px 16px',
                                color: '#FF69B4',
                                fontSize: '15px',
                                lineHeight: '18px',

                            },
                            '& input::placeholder': {
                                color: 'rgba(255,255,255,0.6)',
                                opacity: 1,
                            },
                            '& .MuiFormHelperText-root': {
                                ml: '20px'
                            }
                        }}
                    />
                    <Button
                        variant="contained" startIcon={<Add />} onClick={() => setTrackModalOpen(true)}
                        sx={{
                            border: '1px solid rgba(230, 210, 249, 0.1) !important',
                            color: '#fff',
                            textTransform: 'none',
                            backgroundColor: '#FF69B4 !important',
                            borderRadius: '999px',
                            fontSize: '15px',
                            lineHeight: '24px',
                            padding: '0',
                            minWidth: '38px',
                            height: '38px',
                            '& .MuiButton-startIcon': {
                                margin: 0,
                            },
                        }}
                    >
                    </Button>
                </Box>
            </Box>

            {/* STYLED DATAGRID: FIXED THE WHITE COLOR ISSUES */}
            <Paper sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', px: '12px' }}>
                <DataGrid
                    rows={trackRows}
                    getRowId={(row) => row.id}
                    columns={trackColumns}
                    loading={loading}
                    slots={{ toolbar: GridToolbar, loadingOverlay: CustomLoadingOverlay }}
                    paginationMode="server"
                    rowCount={pagination?.total || 0}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => {
                        setPaginationModel(model);
                    }}
                    slotProps={{
                        toolbar: {
                            csvOptions: { disableToolbarButton: true }, // Removes the "Export as CSV" menu item
                            printOptions: { disableToolbarButton: true }, // Removes the "Print" menu item
                        },
                    }}
                    localeText={
                        i18n.language?.toLowerCase().startsWith("fr")
                            ? frFR.components.MuiDataGrid.defaultProps.localeText
                            : enUS.components.MuiDataGrid.defaultProps.localeText
                    }
                    sx={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: '0',
                        '& .MuiDataGrid-toolbarContainer': {
                            padding: '12px',
                            margin: '0 -12px',
                            gap: '10px',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                        '& .MuiDataGrid-topContainer::after': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
                            color: '#fff',
                            border: '1px solid rgba(230, 210, 249, 0.2)',
                            borderRadius: '999px',
                            textTransform: 'none',
                            fontWeight: 500,
                            padding: '6px 12px',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                        '& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-icon .MuiBadge-root svg': {
                            width: '18px',
                            height: '18px',
                        },
                        '& .MuiDataGrid-virtualScrollerContent': {
                            maxHeight: 'auto',
                            height: 'auto !important'
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            borderRadius: '0 !important',
                        },
                        '& .MuiDataGrid-virtualScrollerRenderZone': {
                            position: 'inherit',
                        },
                        '& .MuiDataGrid-virtualScrollerRenderZone>.MuiDataGrid-row': {
                            maxHeight: 'none !important',
                            height: 'auto !important',
                        },
                        '& .MuiDataGrid-cell': {
                            height: 'auto !important',
                            minHeight: 'var(--height)',
                            paddingTop: '5px',
                            paddingBottom: '5px',
                        },
                        '& .MuiDataGrid-cell>.MuiBox-root': {
                            minHeight: '100%',
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
                            borderRadius: '0 !important',
                            borderColor: 'rgba(230, 210, 249, 0.2) !important',
                            color: '#fff',
                        },
                        '& .MuiDataGrid-columnHeaders [role=row]': {
                            backgroundColor: '#1a1a1a',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderColor: 'rgba(230, 210, 249, 0.2) !important'
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
                            color: 'white',
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                            opacity: '0.8 !important',
                            fill: 'white',
                        },
                        '& .MuiDataGrid-cell .MuiChip-outlined': {
                            border: '0 !important',
                            height: 'auto',
                            fontWeight: '600',
                        },
                        '& .MuiDataGrid-cell .MuiChip-outlined.MuiChip-colorSuccess': {
                            color: "#03c11c",
                        },
                        '& .MuiTablePagination-root': {
                            color: '#fff'
                        },
                        '& .MuiTablePagination-selectLabel': {
                            marginBottom: '0',
                        },
                        '& .MuiTablePagination-input': {
                            marginRight: '10px',
                        },
                        '& .MuiTablePagination-input .MuiSelect-select': {
                            paddingTop: '2px',
                            paddingBottom: '2px',
                            border: '1px solid rgba(230, 210, 249, 0.2)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            borderRadius: '4px',
                        },
                        '& .MuiTablePagination-input .MuiSvgIcon-root': {
                            fill: 'white'
                        },
                        '& .MuiTablePagination-toolbar .MuiTablePagination-displayedRows': {
                            marginBottom: '0',
                        },
                        '& .MuiTablePagination-actions': {
                            marginLeft: '10px !important',
                        },
                        '& .MuiTablePagination-actions .MuiButtonBase-root': {
                            color: '#FF69B4',
                        },
                        '& .MuiDataGrid-filler': {
                            height: 'auto !important',
                        },
                        '& .MuiDataGrid-filler>div': {
                            borderColor: 'transparent',
                        },
                        '& .MuiDataGrid-overlayWrapper': {
                            height: 'auto !important',
                            maxHeight: '50px',
                        },
                        '& .MuiDataGrid-overlayWrapperInner': {
                            minHeight: '40px',
                            height: 'auto !important'
                        },
                        '& .MuiDataGrid-overlay': {
                            color: '#fff',
                            borderTop: '1px solid rgba(230, 210, 249, 0.2)',
                            backgroundColor: 'transparent',
                            minHeight: '40px',
                        },
                    }}
                />
            </Paper>

            <AddTrackModal
                open={trackModalOpen}
                handleClose={() => {
                    setTrackModalOpen(false);
                    setSelectedTrack(null);
                }}
                t={t}
                albumId={selectedAlbum?.id}
                paginationModel={paginationModel}
                searchText={searchText}
                editingTrack={selectedTrack}
                nextTrackNumber={computedNextTrackNumber}
            />

            <ViewTrackModal
                open={viewModalOpen}
                handleClose={() => {
                    setViewModalOpen(false);
                    setSelectedTrack(null);
                }}
                t={t}
                setSelectedTrack={setSelectedTrack}
                viewTrack={selectedTrack}
            />

            <ScheduleRequestModal
                open={scheduleModalOpen}
                handleClose={() => setScheduleModalOpen(false)}
                selectedRecord={scheduleSelectedTrack}
                onSubmit={(data) => {
                    try {
                        dispatch(scheduleTrackRequest({
                            id: scheduleSelectedTrack.id,
                            payload: {
                                publishAt: data.publishAt,
                                timezone: data.timezone,
                                note: data.note
                            }
                        }));
                    } catch (err) {
                        toast.error(err);
                    } finally {
                        setScheduleModalOpen(false);
                        dispatch(fetchTracks({
                            albumId: selectedAlbum.id,
                            page: paginationModel.page + 1,
                            limit: paginationModel.pageSize,
                            search: searchText,
                        }));
                    }
                }}
            />

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center' }}>{t('delete_track', 'Delete Track')}</DialogTitle>
                <DialogContent sx={{ pb: '0' }}><Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center' }}>{"Are you sure?"}</Typography></DialogContent>
                <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>{t('cancel', 'Cancel')}</Button>
                    <Button
                        variant="contained"
                        onClick={handleDeleteConfirm}
                        sx={{
                            py: 1.5,
                            borderRadius: '999px',
                            fontWeight: '400',
                            fontSize: '15px',
                            lineHeight: '20px',
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            textTransform: 'capitalize',
                            minWidth: { xs: '100%', sm: '150px' },
                            marginLeft: '10px',
                        }}
                    >
                        {t('delete', 'Delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={scheduleConfirmDialog.open}
                onClose={() => setScheduleConfirmDialog({ open: false })}
            >
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center' }}>{t("confirm_action", "Confirm Action")}</DialogTitle>
                <DialogContent sx={{ pb: '0' }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center' }}>
                        {t('albums.albumDetails.actions.confirm_schedule', "Are you sure you want to Schedule this request?")}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
                    <Button
                        onClick={() => setScheduleConfirmDialog({ open: false })}
                        sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}
                    >
                        {t("cancel", "Cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setScheduleConfirmDialog({ open: false });
                            setScheduleModalOpen(true);
                        }}
                        sx={{
                            py: 1.5,
                            borderRadius: '999px',
                            fontWeight: '400',
                            fontSize: '15px',
                            lineHeight: '20px',
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            textTransform: 'capitalize',
                            minWidth: { xs: '100%', sm: '150px' },
                            marginLeft: '10px',
                        }}
                    >
                        {t("confirm", "Confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Publish Track Request  */}
            <Dialog open={publishTrackDialogOpen} onClose={() => setPublishTrackDialogOpen(false)}>
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center' }}>{t("confirm_publish", "Confirm Publish")}</DialogTitle>
                <DialogContent sx={{ pb: '0' }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center' }}>{t("albums.albumDetails.actions.track_album_confirmation", "Are you sure you want to publish this track?")}</Typography>
                </DialogContent>
                <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
                    <Button onClick={() => setPublishTrackDialogOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}> {t("cancel", "Cancel")} </Button>
                    <Button
                        variant="contained"
                        disabled={publishing}
                        onClick={handlePublishTrackRequestConfirm}
                        sx={{
                            py: 1.5,
                            borderRadius: '999px',
                            fontWeight: '400',
                            fontSize: '15px',
                            lineHeight: '20px',
                            textTransform: 'none',
                            background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            textTransform: 'capitalize',
                            minWidth: { xs: '100%', sm: '150px' },
                            marginLeft: '10px',
                        }}
                    >
                        {publishing ? <CircularProgress size={20} /> : t("publish", "Publish")}
                    </Button>
                </DialogActions>
            </Dialog>

            <GlobalStyles
                styles={{
                    '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
                        backgroundColor: '#252525 !important',
                        border: '1px solid rgba(230, 210, 249, 0.1)',
                        fontSize: '15px',
                        color: '#fff',
                        borderRadius: '4px',
                    },
                    '.MuiDialog-root .MuiPaper-root': {
                        borderRadius: '20px',
                    },
                    '.MuiDataGrid-columnsManagementHeader': {
                        padding: '10px !important',
                    },
                    '.MuiInputBase-formControl': {
                        fontSize: '14px !important',
                        color: 'white !important',
                        borderRadius: '999px !important',
                    },
                    '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
                        borderTop: '1px solid rgba(230, 210, 249, 0.1)',
                        padding: '4px 8px 4px 16px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement': {
                        padding: '0px 16px 12px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
                        color: '#FF69B4',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
                        color: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
                        borderColor: 'rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
                        display: 'none',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
                        padding: '0',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
                        padding: '5px 10px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: '#FF69B4',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
                        borderColor: 'rgba(230, 210, 249, 0.3) !important',
                        margin: '0',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        minWidth: '32px',
                    },
                    '.MuiDataGrid-scrollbarFiller': {
                        borderTop: 'none !important',
                    },
                    '& .MuiDataGrid-scrollbar': {
                        '&::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(230, 210, 249, 0.3)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: 'rgba(230, 210, 249, 0.5)',
                        },
                    },
                    '& .MuiDataGrid-selectedRowCount': {
                        color: '#fff',
                    },
                }}
            />
        </Box>
    );
}

export default AlbumManagementDetails;
